import { useState } from "react";
import StaticPage from "@/components/StaticPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Mail } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

const TOPICS = [
  { id: "general", label: "General question" },
  { id: "sales", label: "Sales — I'm evaluating NextVisit" },
  { id: "support", label: "Support — I have an issue with my account" },
  { id: "press", label: "Press or partnerships" },
  { id: "dpa", label: "Legal — I need a signed DPA" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      toast.error("Please fill in every field (message needs at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : "Send failed. Try again.");
        return;
      }
      setDone(true);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setSubmitting(false); }
  };

  return (
    <StaticPage eyebrow="Contact us" title="Say hi. We read every message.">
      <p className="text-lg text-[#183D36]">
        The fastest way to reach a real human at NextVisit is this form. We answer within one business day, usually much sooner.
      </p>
      <p className="text-sm text-[#4A4A4A]">
        Prefer email directly? <a className="text-[#183D36] underline" href="mailto:brooke@bookyournextvisit.com">brooke@bookyournextvisit.com</a>
      </p>

      {done ? (
        <div className="rounded-3xl bg-[#A8BFA3]/40 border border-[#A8BFA3] p-8 my-6" data-testid="contact-success">
          <div className="w-12 h-12 rounded-2xl bg-[#183D36] text-[#A8BFA3] flex items-center justify-center mb-3">
            <CheckCircle2 size={22} />
          </div>
          <h3 className="font-heading text-2xl text-[#183D36] mb-2">Got it — thank you.</h3>
          <p className="text-[#252525]">Your message is on its way. We&apos;ll get back to you at <b className="text-[#183D36]">{form.email}</b> within one business day. If it&apos;s urgent, feel free to also email brooke@bookyournextvisit.com directly.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-3xl bg-white border border-[#E5E0D8] p-8 my-6 grid gap-5" data-testid="contact-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="contact-name">Your name</Label>
              <Input id="contact-name" data-testid="contact-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Rivera" required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input id="contact-email" data-testid="contact-input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@business.com" required />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>What&apos;s this about?</Label>
            <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
              <SelectTrigger data-testid="contact-input-topic" className="rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TOPICS.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="contact-message">Message</Label>
            <Textarea
              id="contact-message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us what&apos;s on your mind…"
              rows={6}
              required
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] w-fit px-6"
          >
            <Mail size={14} className="mr-2" />
            {submitting ? "Sending…" : "Send message"}
          </Button>
        </form>
      )}
    </StaticPage>
  );
}
